import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
  Apple Pay + Google Pay buttons via PayPal Advanced Checkout.

  Behaviour:
  - When the backend flag WALLET_PAYMENTS_ENABLED is OFF (current state),
    we show a clean "Coming Soon" placeholder so users know wallet payments
    are on the roadmap.
  - When the flag is ON AND the visitor's device/browser is eligible
    (Apple Pay → Safari/iOS, Google Pay → Chrome with a configured wallet),
    the real PayPal-hosted buttons render.
  - Loads its own PayPal SDK instance under data-namespace="paypal_wallets"
    so it never clashes with the subscription SDK on the same page.
*/

let walletSdkPromise = null;
const loadWalletSdk = (clientId, currency) => {
  if (window.paypal_wallets) return Promise.resolve(window.paypal_wallets);
  if (walletSdkPromise) return walletSdkPromise;
  walletSdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src =
      `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}` +
      `&components=buttons,applepay,googlepay` +
      `&enable-funding=applepay,googlepay` +
      `&currency=${encodeURIComponent(currency)}&intent=capture`;
    script.setAttribute("data-namespace", "paypal_wallets");
    script.async = true;
    script.onload = () => resolve(window.paypal_wallets);
    script.onerror = () => {
      walletSdkPromise = null;
      reject(new Error("Wallet SDK failed to load"));
    };
    document.body.appendChild(script);
  });
  return walletSdkPromise;
};

const ComingSoonCard = () => (
  <div
    data-testid="wallet-coming-soon"
    className="mb-3 rounded-xl border border-dashed border-ink-300 bg-ink-50/60 px-4 py-3 dark:border-ink-700 dark:bg-ink-900/40"
  >
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5">
          {/* Apple Pay glyph */}
          <span className="inline-flex h-8 w-12 items-center justify-center rounded-md bg-black text-white">
            <svg viewBox="0 0 24 24" className="h-3.5 w-auto" fill="currentColor" aria-hidden="true">
              <path d="M17.6 13c0-1.9 1.6-2.9 1.7-2.9-.9-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.3 2-1.4 2.4-.4 6 1 8 .7 1 1.5 2 2.5 2s1.4-.6 2.6-.6c1.2 0 1.5.6 2.6.6s1.8-1 2.5-2c.8-1.1 1.1-2.2 1.1-2.3-.1 0-2.2-.8-2.2-3.2zM15.7 6.7c.5-.6.9-1.5.8-2.4-.8 0-1.7.5-2.2 1.1-.5.5-.9 1.4-.8 2.3.9.1 1.8-.4 2.2-1z"/>
            </svg>
          </span>
          {/* Google Pay glyph */}
          <span className="inline-flex h-8 w-12 items-center justify-center rounded-md border border-ink-300 bg-white">
            <svg viewBox="0 0 41 17" className="h-3.5 w-auto" aria-hidden="true">
              <path fill="#5F6368" d="M19.5 8.3v4.7h-1.5V1.5h4c1 0 1.9.3 2.6 1 .7.7 1.1 1.5 1.1 2.5s-.4 1.9-1.1 2.6c-.7.7-1.6 1-2.6 1h-2.5v-.3zm0-5.4v3.9h2.6c.6 0 1.1-.2 1.5-.6.4-.4.6-.9.6-1.4s-.2-1-.6-1.4c-.4-.4-.9-.6-1.5-.6h-2.6v.1z"/>
              <path fill="#5F6368" d="M28.8 5c1.1 0 2 .3 2.6.9.6.6.9 1.4.9 2.4V13h-1.4v-1.1h-.1c-.6.9-1.4 1.3-2.4 1.3-.9 0-1.6-.3-2.2-.8s-.9-1.2-.9-2c0-.9.3-1.5.9-2 .6-.5 1.4-.7 2.5-.7.9 0 1.6.2 2.2.5v-.3c0-.5-.2-.9-.6-1.3-.4-.4-.9-.5-1.5-.5-.9 0-1.5.4-2.1 1.1l-1.3-.8c.7-1 1.8-1.5 3.4-1.5zm-1.9 5.9c0 .4.2.7.5.9.3.3.7.4 1.2.4.7 0 1.3-.2 1.8-.7.5-.5.8-1 .8-1.7-.5-.4-1.2-.6-2.1-.6-.7 0-1.2.2-1.6.5s-.6.7-.6 1.2z"/>
              <path fill="#5F6368" d="M40.5 5.2L35.4 17h-1.5l1.9-4.1L32.5 5.2H34l2.4 5.9 2.4-5.9z"/>
              <path fill="#4285F4" d="M13.3 7.3c0-.5 0-.9-.1-1.4H6.8V8.5h3.7c-.2.9-.7 1.6-1.5 2.2v1.8h2.4c1.4-1.3 2.2-3.1 2.2-5.2h-.3z"/>
              <path fill="#34A853" d="M6.8 14c1.9 0 3.5-.6 4.7-1.7l-2.4-1.8c-.6.4-1.4.7-2.3.7-1.8 0-3.4-1.2-3.9-2.9H.4v1.8C1.6 12.5 4 14 6.8 14z"/>
              <path fill="#FBBC04" d="M2.9 8.3c-.1-.4-.2-.8-.2-1.2 0-.4.1-.8.2-1.2V4.1H.4C0 5 0 5.9 0 7.1c0 1.2 0 2.1.4 3l2.5-1.8z"/>
              <path fill="#EA4335" d="M6.8 3c1 0 2 .4 2.7 1.1L11.6 2C10.3.9 8.7.2 6.8.2 4 .2 1.6 1.7.4 4l2.5 1.8c.5-1.6 2.1-2.8 3.9-2.8z"/>
            </svg>
          </span>
        </div>
        <div className="leading-tight">
          <p className="text-sm font-semibold text-ink-900 dark:text-ink-50">
            Apple Pay &amp; Google Pay
          </p>
          <p className="text-[11px] text-ink-500 dark:text-ink-400">
            One-tap checkout from your device wallet
          </p>
        </div>
      </div>
      <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
        Coming soon
      </span>
    </div>
  </div>
);

export const WalletPaymentButtons = ({ plan, onSuccess }) => {
  const appleRef = useRef(null);
  const googleRef = useRef(null);
  const [enabled, setEnabled] = useState(null); // null = loading, false = off, true = on
  const [anyEligible, setAnyEligible] = useState(false);

  useEffect(() => {
    if (!plan) return;
    let cancelled = false;

    const setup = async () => {
      try {
        const { data: cfg } = await axios.get(`${API}/payments/wallet/config`);
        if (cancelled) return;

        if (!cfg.enabled || !cfg.client_id) {
          setEnabled(false);
          return;
        }
        setEnabled(true);

        const sdk = await loadWalletSdk(cfg.client_id, cfg.currency || "USD");
        if (!sdk || cancelled) return;

        const createOrder = async () => {
          const r = await axios.post(`${API}/payments/wallet/create-order`, { plan });
          return r.data.id;
        };

        const onApprove = async (data) => {
          try {
            const r = await axios.post(`${API}/payments/wallet/capture-order`, {
              order_id: data.orderID,
            });
            if (r.data.status === "COMPLETED") {
              toast.success("Payment confirmed — your plan is active for 30 days!");
              onSuccess?.();
            }
          } catch (e) {
            toast.error(e.response?.data?.detail || "Payment could not be completed.");
          }
        };

        const onError = (err) => {
          console.error("Wallet payment error:", err);
          toast.error("Payment failed. Please try again or use another method.");
        };

        let eligible = false;
        for (const [source, ref] of [
          [sdk.FUNDING?.APPLEPAY, appleRef],
          [sdk.FUNDING?.GOOGLEPAY, googleRef],
        ]) {
          if (!source || !ref.current) continue;
          const btn = sdk.Buttons({
            fundingSource: source,
            style: { height: 44 },
            createOrder,
            onApprove,
            onError,
          });
          if (btn.isEligible()) {
            ref.current.innerHTML = "";
            btn.render(ref.current);
            eligible = true;
          }
        }
        if (!cancelled) setAnyEligible(eligible);
      } catch {
        if (!cancelled) setEnabled(false);
      }
    };

    setup();
    return () => { cancelled = true; };
  }, [plan]);

  // Loading state — render nothing while we resolve the config
  if (enabled === null) return null;

  // Feature flag OFF → show polished "Coming soon" card
  if (enabled === false) return <ComingSoonCard />;

  // Feature flag ON but device not eligible → hide silently (user has other payment options)
  if (!anyEligible) {
    return (
      <div className="hidden">
        <div ref={appleRef} data-testid="apple-pay-button-container" />
        <div ref={googleRef} data-testid="google-pay-button-container" />
      </div>
    );
  }

  // Feature flag ON + eligible → render the real wallet buttons
  return (
    <div data-testid="wallet-payment-buttons" className="space-y-2 mb-3">
      <div ref={appleRef} data-testid="apple-pay-button-container" />
      <div ref={googleRef} data-testid="google-pay-button-container" />
      <div className="relative flex items-center">
        <span className="flex-grow border-t border-ink-200"></span>
        <span className="mx-3 text-[10px] font-bold uppercase tracking-[0.22em] text-ink-400">
          or
        </span>
        <span className="flex-grow border-t border-ink-200"></span>
      </div>
    </div>
  );
};
